import * as React from "react";
import { observer } from "mobx-react";
import { DealsProps } from "./typings";
import { withStores } from "stores";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { CampaignHeader } from "../../shared/Merchandising/Common/CampaignHeader/CampaignHeader";
import { useLocalization } from "@shared-ui/localization-context";
import { DealsPlaceholder } from "./DealsPlaceHolder";
import { GridPlaceholder } from "./GridPlaceHolder";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { AuthenticationState } from "bernie-context";
import { CampaignByIdQuery } from "../../../../__generated__/api";
import Campaigns = CampaignByIdQuery.Campaigns;
import Offers = CampaignByIdQuery.Offers;
import { MerchCampaignCard } from "components/shared/Merchandising/Common/MerchCampaignCard/MerchCampaignCard";
import { DealsErrorFallback } from "./DealsErrorFallback";

const Deals = withStores(
  "routeStore",
  "dealsStore",
  "flexViewModel"
)(
  observer((props: DealsProps) => {
    const { flexComponent, flexViewModel } = props;
    const model = flexComponent.model;
    const flexContext = flexViewModel.flexContext;
    const { formatText } = useLocalization();
    const keyHelper = new ItemKeyHelper(flexComponent.id);
    const campaignId = model?.campaignId;
    const userContext = flexContext.userContext;
    const lat = props.routeStore.queryParams.lat;
    const long = props.routeStore.queryParams.long;

    const [campaign, setCampaign] = React.useState<Campaigns>();
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [campaignFetchFailed, setCampaignFetchFailed] = React.useState<boolean>(false);

    if (!flexComponent.model) {
      return null;
    }

    const {
      destinationView,
      hotelInventoryView,
      displayDate,
      displayDescription,
      displayPrice,
      displayPropertyCount,
    } = flexComponent.model;

    React.useEffect(() => {
      fetchCampaigns();
    }, []);

    const fetchCampaigns = async () => {
      if (shouldRender()) {
        props.dealsStore
          .fetchCampaignDeals(
            props.context,
            new Array(campaignId),
            model.enableTripAttach,
            lat ? lat : userContext.userLocation?.latitude,
            long ? long : userContext.userLocation?.longitude,
            model.displayPropertyCount,
            flexViewModel.trackingContext?.pageName
          )
          .then((result) => {
            setCampaign(result);
            setIsLoading(false);
            setCampaignFetchFailed(result === null);
          });
      }
    };

    const openDialog = (dialogPath: string) => {
      props.routeStore.updateSearch({ pwaDialog: dialogPath });
    };

    const closeDialog = () => {
      props.routeStore.closeDialog("pwaDialog");
    };

    const shouldRender = () => {
      return (
        campaignId &&
        (!model.enableTripAttach ||
          (model.enableTripAttach && userContext.userType === AuthenticationState.AUTHENTICATED))
      );
    };

    if (!shouldRender()) {
      return null;
    }

    const showErrorFallBack = () => {
      return model.isFeatured && (campaignFetchFailed || !hasEnoughDeals());
    };

    const hasEnoughDeals = () => {
      return campaign?.offers?.length && campaign?.offers?.length >= 4;
    };

    const renderMerchCampaignCard = (c: Campaigns, o: Offers, index: number) => {
      return (
        <MerchCampaignCard
          offer={o}
          cardIndex={index}
          campaignId={c.id}
          productTypes={c.productTypes}
          recommendedType={c.recommendedType}
          openDialog={openDialog}
          closeDialog={closeDialog}
          destinationView={destinationView}
          hotelInventoryView={hotelInventoryView}
          displayDate={displayDate}
          displayDescription={displayDescription}
          displayPrice={displayPrice}
          displayPropertyCount={displayPropertyCount}
          withBorder={true}
          moduleName="Deals"
        />
      );
    };

    const isGrid = model.view === "grid";

    return isLoading ? (
      isGrid ? (
        <GridPlaceholder flexComponentId={flexComponent.id} />
      ) : (
        <DealsPlaceholder flexComponentId={flexComponent.id} />
      )
    ) : (
      <>
        {showErrorFallBack() && <DealsErrorFallback />}

        {campaign && hasEnoughDeals() && (
          <div
            data-testid="deals"
            className="Deals uitk-cell all-b-margin-six"
            id={flexComponent.id}
            data-fm={flexComponent.fmId}
          >
            <div className="all-t-margin-six" data-testid="deals-carousel">
              <CampaignHeader
                campaign={campaign}
                openDialog={openDialog}
                closeDialog={closeDialog}
                campaignTitle={model.title}
                campaignCopyAlignment={model.campaignCopyAlignment}
                showCampaignDescription={model.showCampaignDescription}
                showCampaignTitle={model.showCampaignTitle}
                showCampaignTermsAndConditions={model.showTermsAndConditions}
                view={model.view}
                imgSrc={model.heroImageSrc}
              />
              {model.view === "carousel-vr" && (
                <Viewport>
                  <ViewSmall>
                    <IsomorphicCarousel
                      className="carouselComponent all-t-margin-three"
                      itemsVisible={{
                        lg: 2,
                        md: 2,
                        sm: 2,
                      }}
                      buttonText={{
                        nextButton: formatText("carousel.item.next"),
                        prevButton: formatText("carousel.item.prev"),
                      }}
                      pageBy={1}
                      peekWidth={10}
                    >
                      {campaign.offers?.slice(0, 4).map((offer, cardIndex) => {
                        return (
                          <div className="uitk-height-item uitk-cell" key={keyHelper.next()}>
                            {renderMerchCampaignCard(campaign, offer, cardIndex)}
                          </div>
                        );
                      }) || []}
                    </IsomorphicCarousel>
                  </ViewSmall>
                  <ViewMedium>
                    <UitkLayoutGrid columns={4} space="three" className="all-x-margin-three all-x-padding-twelve">
                      {campaign.offers?.slice(0, 4).map((offer, cardIndex) => {
                        return (
                          <div className="uitk-height-item" key={keyHelper.next()} data-testid="deals-grid">
                            {renderMerchCampaignCard(campaign, offer, cardIndex)}
                          </div>
                        );
                      }) || []}
                    </UitkLayoutGrid>
                  </ViewMedium>
                </Viewport>
              )}
              {(model.view === "carousel" || model.view == null) && (
                <IsomorphicCarousel
                  className="carouselComponent all-t-margin-three"
                  itemsVisible={{
                    lg: 3,
                    md: 2,
                    sm: 1,
                  }}
                  buttonText={{
                    nextButton: formatText("carousel.item.next"),
                    prevButton: formatText("carousel.item.prev"),
                  }}
                  pageBy={1}
                  peekWidth={24.8}
                >
                  {campaign.offers?.map((offer, cardIndex) => {
                    return (
                      <div className="uitk-height-item uitk-cell" key={keyHelper.next()}>
                        {renderMerchCampaignCard(campaign, offer, cardIndex)}
                      </div>
                    );
                  }) || []}
                </IsomorphicCarousel>
              )}
              {isGrid && (
                <UitkLayoutGrid
                  columns={{ medium: 2, extra_large: 3, small: 1 }}
                  space="three"
                  className="all-l-margin-three all-r-margin-three all-t-margin-three"
                >
                  {campaign.offers?.map((offer, cardIndex) => {
                    return (
                      <div className="uitk-height-item uitk-cell" key={keyHelper.next()} data-testid="deals-grid">
                        {renderMerchCampaignCard(campaign, offer, cardIndex)}
                      </div>
                    );
                  }) || []}
                </UitkLayoutGrid>
              )}
            </div>
          </div>
        )}
      </>
    );
  })
);

export default Deals;
